/* eslint-disable react/forbid-prop-types */
import { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
    Box, Typography, TextField, InputAdornment, CircularProgress,
    Autocomplete, Avatar, alpha, Stack, Button, Chip, Tooltip,
} from '@mui/material';
import { debounce } from 'lodash';
import Iconify from 'src/components/Iconify';
import { callApi } from 'src/utils/apiCall';
import useCustomSnackbar from 'src/hooks/useCustomSnackbar';
import { common } from 'src/theme/palette';
import { isValidEmail, isValidPhone } from './quickSendUtils';

// Same field projection PatientSelector uses.
const SEARCH_FIELDS = 'id,first_name,last_name,lead_contact';

const getName = (lead) =>
    `${lead?.first_name || ''} ${lead?.last_name || ''}`.trim() || 'Unnamed';
const getInitials = (lead) =>
    `${(lead?.first_name || '?')[0] || '?'}${(lead?.last_name || '?')[0] || ''}`.toUpperCase();

// Three recipient lanes — making them explicit removes the "too many CTAs"
// problem from the v1 implementation. Each lane swaps the body in; the
// header pill row stays static so the user always sees the three options.
const LANES = [
    {
        value: 'EXISTING',
        label: 'Existing patient',
        icon: 'eva:people-outline',
        helper: 'Pick someone already in your patient list.',
    },
    {
        value: 'NEW',
        label: 'New patient',
        icon: 'eva:person-add-outline',
        helper: 'Create a patient record, or send without saving one.',
    },
    {
        value: 'ANONYMOUS',
        label: 'Public link',
        icon: 'eva:link-2-outline',
        helper: 'Generate a shareable link — no patient record needed.',
    },
];

/**
 * Step 3 — Recipient. Three explicit lanes:
 *
 *  - EXISTING  → debounced search Autocomplete over /leads/search/.
 *  - NEW       → inline create-lead form (mounted immediately; first name +
 *                email-or-phone required).
 *  - ANONYMOUS → explanatory card with a single "Generate public link" CTA
 *                that resolves to Step 4 with a fresh URL.
 *
 * The parent (`QuickSend.jsx`) drives the actual lead-creation /
 * link-generation flow; this component is responsible for keeping the
 * UI lanes tidy and giving the user one clear path per lane.
 */
const QuickSendStep3Patient = ({
    practiceId,
    selectedForms,
    selectedLead,
    onSelectLead,
    onSkipLeadAnonymous,
    isGeneratingLink,
    highFidelityPdf,
    setHighFidelityPdf,
}) => {
    const { enqueueSnackbar } = useCustomSnackbar();
    const activePractice = useRef(practiceId);
    activePractice.current = practiceId;
    useEffect(() => {
        activePractice.current = practiceId;
        return () => { activePractice.current = null; };
    }, [practiceId]);

    // Lane state. Defaults to EXISTING; auto-flips to EXISTING when a lead
    // is selected (so the success pill is visible in the right lane).
    const [lane, setLane] = useState('EXISTING');
    useEffect(() => {
        if (selectedLead) setLane('EXISTING');
    }, [selectedLead]);

    // ── EXISTING lane: search state ─────────────────────────────────────────
    const [inputValue, setInputValue] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [searchAttempted, setSearchAttempted] = useState(false);
    const searchIntent = useRef(0);

    const doSearch = useCallback(
        debounce(async (query, intent) => {
            if (intent !== searchIntent.current) return;
            if (!query || query.length < 2 || !practiceId) {
                setSearchResults([]);
                setSearchLoading(false);
                setSearchAttempted(false);
                return;
            }
            setSearchLoading(true);
            setSearchAttempted(false);
            try {
                const res = await callApi(
                    `/api/v1/practices/${practiceId}/leads/search/?query=${encodeURIComponent(query)}&limit=10&offset=0&ordering=-created_at&is_archived=false&fields={${SEARCH_FIELDS}}`,
                );
                if (activePractice.current !== practiceId || intent !== searchIntent.current) return;
                if ((res.ok || res.status === 200) && res.data?.results) {
                    setSearchResults(res.data.results);
                } else {
                    setSearchResults([]);
                }
            } catch {
                if (activePractice.current === practiceId && intent === searchIntent.current) setSearchResults([]);
            } finally {
                if (activePractice.current === practiceId && intent === searchIntent.current) {
                    setSearchLoading(false);
                    setSearchAttempted(true);
                }
            }
        }, 600),
        [practiceId],
    );

    useEffect(() => () => doSearch.cancel(), [doSearch]);

    const zeroResults = (
        !selectedLead
        && searchAttempted
        && !searchLoading
        && inputValue.trim().length >= 2
        && searchResults.length === 0
    );

    // ── NEW lane: create-lead state ──────────────────────────────────────────
    // Seed from the EXISTING-lane search query when the user explicitly flips
    // to NEW (so a "couldn't find them — let's create" handoff is smooth).
    const [createFirstName, setCreateFirstName] = useState('');
    const [createLastName, setCreateLastName] = useState('');
    const [createEmail, setCreateEmail] = useState('');
    const [createPhone, setCreatePhone] = useState('');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState('');

    const handleLaneChange = (next) => {
        if (next === lane || creating || isGeneratingLink) return;
        searchIntent.current += 1;
        doSearch.cancel();
        setSearchLoading(false);
        setSearchResults([]);
        setSearchAttempted(false);
        if (next === 'NEW' && !createFirstName) {
            // Seed first/last from the existing search query — split on
            // first whitespace boundary.
            const parts = (inputValue || '').trim().split(/\s+/);
            if (parts[0]) setCreateFirstName(parts[0]);
            if (parts.length > 1) setCreateLastName(parts.slice(1).join(' '));
        }
        if (next !== 'EXISTING') onSelectLead(null);
        setLane(next);
    };

    const canCreate = (
        createFirstName.trim().length > 0
        && (createEmail.trim() !== '' || createPhone.trim() !== '')
    );

    const handleCreateLead = async () => {
        if (!canCreate || creating || isGeneratingLink) return;
        if (createEmail && !isValidEmail(createEmail)) {
            setCreateError('Please enter a valid email address.');
            return;
        }
        if (createPhone && !isValidPhone(createPhone)) {
            setCreateError('Please enter a valid phone number.');
            return;
        }
        setCreateError('');
        setCreating(true);
        try {
            const payload = {
                first_name: createFirstName.trim(),
                last_name: createLastName.trim() || '',
                source: 'QUICK_SEND_INLINE',
                lead_contact: {
                    email: createEmail.trim() || '',
                    phone: createPhone.trim() || '',
                },
            };
            const res = await callApi(
                `/api/v1/practices/${practiceId}/leads/`,
                'post',
                payload,
                false,
                { timeout: 30000 },
            );
            if (activePractice.current !== practiceId) return;
            if (res?.status === 201 && res?.data?.id) {
                enqueueSnackbar('Patient created. Continue to generate the form link.', { variant: 'success' });
                onSelectLead(res.data);
            } else {
                const detail = typeof res?.data === 'string'
                    ? res.data
                    : (res?.data?.detail
                        || (res?.data?.lead_contact?.email && `Email: ${res.data.lead_contact.email[0]}`)
                        || (res?.data?.lead_contact?.phone && `Phone: ${res.data.lead_contact.phone[0]}`)
                        || 'Failed to create lead');
                setCreateError(detail);
                enqueueSnackbar('Failed to create lead', { variant: 'error' });
            }
        } catch {
            setCreateError('Failed to create lead. Please try again.');
            enqueueSnackbar('Failed to create lead', { variant: 'error' });
        }
        setCreating(false);
    };

    // ── Render ───────────────────────────────────────────────────────────────
    const activeLane = LANES.find((l) => l.value === lane) || LANES[0];

    return (
        <Box>
            <Typography
                sx={{
                    fontSize: '10px', fontWeight: 800, letterSpacing: '0.08em',
                    color: common.mintyZest, textTransform: 'uppercase', mb: 0.5,
                }}
            >
                Step 3 · Recipient
            </Typography>
            <Typography sx={{ fontSize: '15px', fontWeight: 700, mb: 1.5 }}>
                Choose a recipient
            </Typography>

            {/* Lane pills — explicit segmented control for the three journeys.
                Replaces the legacy "Search + Create + Skip + Multi" CTA pileup,
                which competed for the same screen space without a primary
                path. Now exactly one path is active at a time. */}
            <Box
                role="tablist"
                sx={{
                    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: 0.5, p: 0.5, borderRadius: '10px', mb: 1.25,
                    bgcolor: 'rgba(145,158,171,0.12)',
                }}
            >
                {LANES.map((l) => {
                    const active = lane === l.value;
                    return (
                        <Button
                            key={l.value}
                            role="tab"
                            aria-selected={active}
                            onClick={() => handleLaneChange(l.value)}
                            disabled={creating || isGeneratingLink}
                            disableRipple
                            startIcon={<Iconify icon={l.icon} width={14} />}
                            sx={{
                                minHeight: 32, py: 0.5, px: 0.75,
                                fontSize: '12px', fontWeight: 600, textTransform: 'none',
                                borderRadius: '7px',
                                color: active ? '#1C252E' : 'text.secondary',
                                bgcolor: active ? '#fff' : 'transparent',
                                boxShadow: active ? '0 1px 3px rgba(0,0,0,0.08)' : 'none',
                                '&:hover': {
                                    bgcolor: active ? '#fff' : 'rgba(145,158,171,0.16)',
                                },
                            }}
                        >
                            {l.label}
                        </Button>
                    );
                })}
            </Box>
            <Typography
                variant="caption"
                sx={{ display: 'block', mb: 1.5, color: 'text.secondary', fontSize: '11.5px' }}
            >
                {activeLane.helper}
            </Typography>

            {/* EXISTING lane ─ search Autocomplete + selected pill ──────────── */}
            {lane === 'EXISTING' && (
                <>
                    <Autocomplete
                        fullWidth
                        filterOptions={(x) => x}
                        options={searchResults}
                        loading={searchLoading}
                        value={selectedLead}
                        inputValue={inputValue}
                        onChange={(_, newVal) => {
                            onSelectLead(newVal);
                        }}
                        onInputChange={(_, val, reason) => {
                            setInputValue(val);
                            if (reason === 'input') {
                                setSearchAttempted(false);
                                searchIntent.current += 1;
                                doSearch(val, searchIntent.current);
                            }
                            if (reason === 'clear') {
                                searchIntent.current += 1;
                                doSearch.cancel();
                                setSearchLoading(false);
                                setSearchResults([]);
                                setSearchAttempted(false);
                            }
                        }}
                        getOptionLabel={(opt) => getName(opt)}
                        isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                        // Compact popper — about three full rows visible, the
                        // rest scroll. ``mb`` on the listbox (NOT padding on
                        // the Paper — MUI's internal styles override that)
                        // forces a clear 16px white band between the last row
                        // and the Paper's rounded bottom edge so the curve is
                        // unambiguously visible. Mirrors Steps 1 and 2.
                        ListboxProps={{
                            sx: {
                                maxHeight: 260,
                                pt: 0.5,
                                pb: 0,
                                mt: 0,
                                mb: 2,
                                scrollbarGutter: 'stable',
                                '&::-webkit-scrollbar': { width: 6 },
                                '&::-webkit-scrollbar-track': { background: 'transparent' },
                                '&::-webkit-scrollbar-thumb': {
                                    background: 'rgba(145,158,171,0.32)',
                                    borderRadius: 6,
                                },
                                '&::-webkit-scrollbar-thumb:hover': {
                                    background: 'rgba(145,158,171,0.48)',
                                },
                            },
                        }}
                        // Flip upward when the trigger sits low in the dialog
                        // and prevent overflow on tight viewports.
                        //
                        // CRITICAL: `src/App.css` ships a global rule
                        // `.MuiAutocomplete-popper { background: #fff }` which
                        // paints an unrounded white rectangle BEHIND the Paper
                        // — that's the square-bottom-corners leak in the
                        // screenshot. Force the Popper's background back to
                        // transparent here (sx beats the global class selector
                        // on specificity) so only the Paper is visible. Same
                        // fix as QuickSendStep1Forms.jsx / Step2Doctor.jsx.
                        slotProps={{
                            popper: {
                                sx: {
                                    background: 'transparent !important',
                                    backgroundColor: 'transparent !important',
                                },
                                modifiers: [
                                    { name: 'flip', enabled: true },
                                    { name: 'preventOverflow', enabled: true, options: { altAxis: true, padding: 8 } },
                                ],
                            },
                        }}
                        componentsProps={{
                            paper: {
                                sx: {
                                    mt: 0.75,
                                    borderRadius: '18px',
                                    boxShadow: '0 20px 48px rgba(0,0,0,0.16), 0 2px 10px rgba(0,0,0,0.05)',
                                    border: '1px solid',
                                    borderColor: 'rgba(145,158,171,0.2)',
                                    overflow: 'hidden',
                                    bgcolor: '#fff',
                                    '& .MuiAutocomplete-listbox': {
                                        marginBottom: '16px !important',
                                    },
                                    '& .MuiAutocomplete-listbox > li:last-of-type, & .MuiAutocomplete-listbox > li > ul > li:last-of-type': {
                                        borderBottom: 'none',
                                    },
                                },
                            },
                        }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                size="small"
                                placeholder="Search by name, email or phone…"
                                InputProps={{
                                    ...params.InputProps,
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <Iconify icon="eva:search-fill" width={18} sx={{ color: 'text.disabled' }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <>
                                            {searchLoading && <CircularProgress size={14} />}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                                sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }}
                            />
                        )}
                        renderOption={(props, lead) => (
                            <Box
                                component="li"
                                {...props}
                                key={lead.id}
                                sx={{
                                    display: 'flex', alignItems: 'center', gap: 1.25,
                                    py: 1, px: 1.5,
                                    borderBottom: '1px solid',
                                    borderColor: 'rgba(145,158,171,0.08)',
                                    transition: 'background-color 100ms ease',
                                    '&:last-of-type': { borderBottom: 'none' },
                                    '&.Mui-focused, &:hover': {
                                        bgcolor: `${alpha(common.mintyZest, 0.06)} !important`,
                                    },
                                }}
                            >
                                <Avatar
                                    sx={{
                                        width: 32, height: 32, fontSize: '11.5px', fontWeight: 700,
                                        bgcolor: alpha('#078DEE', 0.12), color: '#078DEE',
                                    }}
                                >
                                    {getInitials(lead)}
                                </Avatar>
                                <Box sx={{ flex: 1, minWidth: 0 }}>
                                    <Typography sx={{ fontWeight: 600, fontSize: '13px', lineHeight: 1.3 }} noWrap>
                                        {getName(lead)}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.125 }}>
                                        {lead.lead_contact?.email && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }} noWrap>
                                                {lead.lead_contact.email}
                                            </Typography>
                                        )}
                                        {lead.lead_contact?.email && lead.lead_contact?.phone && (
                                            <Box component="span" sx={{ color: 'rgba(145,158,171,0.4)', fontSize: '10px' }}>·</Box>
                                        )}
                                        {lead.lead_contact?.phone && (
                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '11px' }} noWrap>
                                                {lead.lead_contact.phone}
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        )}
                        noOptionsText={
                            inputValue.length < 2
                                ? 'Type at least 2 characters to search'
                                : 'No patients match. Try "New patient" to create one.'
                        }
                    />

                    {/* Zero-results inline nudge → bridges the user to the
                        NEW-patient lane without forcing them to find the
                        third pill. */}
                    {zeroResults && (
                        <Box
                            sx={{
                                mt: 1, p: 1.25, borderRadius: '10px',
                                bgcolor: alpha(common.mintyZest, 0.06),
                                border: '1px dashed', borderColor: alpha(common.mintyZest, 0.28),
                                display: 'flex', alignItems: 'center', gap: 1,
                            }}
                        >
                            <Iconify icon="eva:person-add-outline" width={16} sx={{ color: common.mintyZest }} />
                            <Typography sx={{ fontSize: '12px', flex: 1, color: 'text.secondary' }}>
                                Can&apos;t find <strong>{inputValue.trim()}</strong>? Create them as a new patient.
                            </Typography>
                            <Button
                                size="small"
                                onClick={() => handleLaneChange('NEW')}
                                sx={{
                                    textTransform: 'none', fontSize: '11.5px', fontWeight: 700,
                                    color: common.mintyZest,
                                    '&:hover': { bgcolor: alpha(common.mintyZest, 0.06) },
                                }}
                            >
                                Create new
                            </Button>
                        </Box>
                    )}

                    {selectedLead && (
                        <Box
                            sx={{
                                mt: 1.5, p: 1.5, borderRadius: '12px',
                                bgcolor: alpha(common.mintyZest, 0.06),
                                border: '1px solid', borderColor: alpha(common.mintyZest, 0.2),
                                display: 'flex', alignItems: 'center', gap: 1.25,
                            }}
                        >
                            <Avatar
                                sx={{
                                    width: 36, height: 36, fontSize: '13px', fontWeight: 700,
                                    bgcolor: alpha(common.mintyZest, 0.15), color: common.mintyZest,
                                }}
                            >
                                {getInitials(selectedLead)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: '14px' }} noWrap>
                                    {getName(selectedLead)}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    {selectedLead.lead_contact?.email
                                        || selectedLead.lead_contact?.phone
                                        || 'No contact info'}
                                </Typography>
                            </Box>
                            <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: common.mintyZest, width: 22 }} />
                        </Box>
                    )}
                </>
            )}

            {/* NEW lane ─ inline create-lead form ─────────────────────────── */}
            {lane === 'NEW' && !selectedLead && (
                <Box
                    sx={{
                        p: 1.75, borderRadius: '12px',
                        bgcolor: alpha(common.mintyZest, 0.04),
                        border: '1px solid', borderColor: alpha(common.mintyZest, 0.2),
                    }}
                >
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
                            gap: 1,
                        }}
                    >
                        <TextField
                            size="small"
                            label="First name"
                            required
                            value={createFirstName}
                            onChange={(e) => setCreateFirstName(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px' } }}
                        />
                        <TextField
                            size="small"
                            label="Last name"
                            value={createLastName}
                            onChange={(e) => setCreateLastName(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px' } }}
                        />
                        <TextField
                            size="small"
                            label="Email"
                            type="email"
                            placeholder="patient@example.com"
                            value={createEmail}
                            onChange={(e) => setCreateEmail(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px' } }}
                        />
                        <TextField
                            size="small"
                            label="Phone"
                            placeholder="+44 7…"
                            value={createPhone}
                            onChange={(e) => setCreatePhone(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#fff', borderRadius: '8px' } }}
                        />
                    </Box>
                    {createError && (
                        <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.75 }}>
                            {createError}
                        </Typography>
                    )}
                    <Stack direction="row" alignItems="center" flexWrap="wrap" gap={1.25} sx={{ mt: 1.25 }}>
                        <Button
                            size="small"
                            variant="contained"
                            disabled={!canCreate || creating || isGeneratingLink}
                            onClick={handleCreateLead}
                            endIcon={creating
                                ? <CircularProgress size={12} sx={{ color: '#fff' }} />
                                : <Iconify icon="eva:arrow-forward-fill" width={14} />}
                            sx={{
                                bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                                fontWeight: 700, fontSize: '12px', borderRadius: '8px',
                                px: 1.75, py: 0.5, minHeight: 32,
                                '&:hover': { bgcolor: '#007B40' },
                                '&.Mui-disabled': { bgcolor: alpha(common.mintyZest, 0.3), color: '#fff' },
                            }}
                        >
                            {creating ? 'Creating…' : 'Create patient'}
                        </Button>
                        <Button
                            size="small"
                            variant="outlined"
                            type="button"
                            disabled={creating || isGeneratingLink || selectedForms.length === 0}
                            onClick={() => {
                                if (!creating && !isGeneratingLink) onSkipLeadAnonymous('EMAIL', { email: createEmail.trim(), phone: createPhone.trim() });
                            }}
                            sx={{
                                textTransform: 'none', fontSize: '12px', fontWeight: 700,
                                color: common.mintyZest, borderColor: common.mintyZest,
                                borderRadius: '8px', px: 1.75, py: 0.5, minHeight: 32,
                                '&:hover': { borderColor: common.mintyZest, bgcolor: alpha(common.mintyZest, 0.06) },
                            }}
                        >
                            Continue without saving
                        </Button>
                    </Stack>
                    <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.75 }}>
                        Choose how to continue. Nothing is sent until the next step.
                    </Typography>
                </Box>
            )}

            {lane === 'NEW' && selectedLead && (
                <Box
                    sx={{
                        p: 1.5, borderRadius: '12px',
                        bgcolor: alpha(common.mintyZest, 0.06),
                        border: '1px solid', borderColor: alpha(common.mintyZest, 0.2),
                        display: 'flex', alignItems: 'center', gap: 1.25,
                    }}
                >
                    <Avatar
                        sx={{
                            width: 36, height: 36, fontSize: '13px', fontWeight: 700,
                            bgcolor: alpha(common.mintyZest, 0.15), color: common.mintyZest,
                        }}
                    >
                        {getInitials(selectedLead)}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 700, fontSize: '14px' }} noWrap>
                            {getName(selectedLead)} · created
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                            {selectedLead.lead_contact?.email
                                || selectedLead.lead_contact?.phone
                                || 'No contact info'}
                        </Typography>
                    </Box>
                    <Iconify icon="eva:checkmark-circle-2-fill" sx={{ color: common.mintyZest, width: 22 }} />
                </Box>
            )}

            {/* High-fidelity PDF — pre-flight-time toggle. Persisted onto
                the ConsentRequest at creation in `QuickSend.generateLink`;
                lives HERE (not Step 4) because by the time Step 4 mounts
                the pre-flight has already fired and the CR's flag is
                frozen. Demoted to a tiny click-to-toggle chip in the
                lower-right of the step body so it never crowds the
                primary recipient picker — defaults ON and the rare
                opt-out is one click away via the chip. */}
            {typeof setHighFidelityPdf === 'function' && (
                <Box sx={{ mt: 1, display: 'flex', justifyContent: 'flex-end' }}>
                    <Tooltip
                        title={highFidelityPdf
                            ? 'High-fidelity PDF · ON — patient’s browser captures the form exactly as shown. Click to use the server fallback. Locks when the link is generated.'
                            : 'High-fidelity PDF · OFF — server fallback (simpler layout, no images or styled signatures). Click to re-enable. Locks when the link is generated.'}
                    >
                        <Chip
                            size="small"
                            icon={(
                                <Iconify
                                    icon="eva:file-text-outline"
                                    width={11}
                                />
                            )}
                            label={`HD PDF · ${highFidelityPdf ? 'On' : 'Off'}`}
                            onClick={() => setHighFidelityPdf(!highFidelityPdf)}
                            aria-label={`Toggle high-fidelity PDF, currently ${highFidelityPdf ? 'on' : 'off'}`}
                            sx={{
                                height: 20,
                                fontSize: '10.5px',
                                fontWeight: 600,
                                letterSpacing: '0.02em',
                                color: highFidelityPdf ? common.mintyZest : 'text.disabled',
                                bgcolor: highFidelityPdf
                                    ? alpha(common.mintyZest, 0.08)
                                    : 'rgba(145,158,171,0.08)',
                                border: '1px solid',
                                borderColor: highFidelityPdf
                                    ? alpha(common.mintyZest, 0.28)
                                    : 'rgba(145,158,171,0.2)',
                                '& .MuiChip-icon': {
                                    color: highFidelityPdf ? common.mintyZest : 'text.disabled',
                                    ml: '5px',
                                    mr: '-3px',
                                },
                                '& .MuiChip-label': { px: 0.75 },
                                '&:hover': {
                                    bgcolor: highFidelityPdf
                                        ? alpha(common.mintyZest, 0.14)
                                        : 'rgba(145,158,171,0.14)',
                                },
                            }}
                        />
                    </Tooltip>
                </Box>
            )}

            {/* ANONYMOUS lane ─ explanatory card + single-CTA Generate ─────── */}
            {lane === 'ANONYMOUS' && (
                <Box
                    sx={{
                        p: 2, borderRadius: '12px',
                        bgcolor: alpha('#078DEE', 0.04),
                        border: '1px solid', borderColor: alpha('#078DEE', 0.2),
                    }}
                >
                    <Stack direction="row" alignItems="flex-start" gap={1.25}>
                        <Box
                            sx={{
                                width: 36, height: 36, borderRadius: '10px',
                                bgcolor: alpha('#078DEE', 0.12), color: '#078DEE',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                flexShrink: 0,
                            }}
                        >
                            <Iconify icon="eva:link-2-outline" width={18} />
                        </Box>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: '13.5px', mb: 0.25 }}>
                                Public link, no patient record
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11.5px' }}>
                                Create a shareable link or QR code without creating a patient record.
                            </Typography>
                        </Box>
                    </Stack>
                    <Typography variant="body2" sx={{ mt: 1 }}>Selected forms: {selectedForms.map((form) => form.name).join(', ') || 'None — go back and choose a form.'}</Typography>
                    <Button
                        fullWidth
                        size="medium"
                        onClick={() => onSkipLeadAnonymous('COPY')}
                        disabled={isGeneratingLink || selectedForms.length === 0}
                        startIcon={isGeneratingLink
                            ? <CircularProgress size={14} sx={{ color: '#fff' }} />
                            : <Iconify icon="eva:flash-outline" width={16} />}
                        sx={{
                            mt: 1.5, bgcolor: common.mintyZest, color: '#fff', textTransform: 'none',
                            fontWeight: 700, fontSize: '13px', borderRadius: '10px',
                            py: 0.875,
                            '&:hover': { bgcolor: '#007B40' },
                            '&.Mui-disabled': { bgcolor: alpha(common.mintyZest, 0.4), color: '#fff' },
                        }}
                    >
                        {isGeneratingLink && 'Generating…'}
                        {!isGeneratingLink && 'Generate public link'}
                    </Button>
                </Box>
            )}
        </Box>
    );
};

QuickSendStep3Patient.propTypes = {
    selectedForms: PropTypes.array,
    practiceId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    selectedLead: PropTypes.object,
    onSelectLead: PropTypes.func.isRequired,
    onSkipLeadAnonymous: PropTypes.func.isRequired,
    isGeneratingLink: PropTypes.bool,
    highFidelityPdf: PropTypes.bool,
    setHighFidelityPdf: PropTypes.func,
};

QuickSendStep3Patient.defaultProps = {
    selectedForms: [],
    selectedLead: null,
    isGeneratingLink: false,
    highFidelityPdf: true,
    setHighFidelityPdf: null,
};

export default QuickSendStep3Patient;
